/**
 * Game cues played through one reusable media element.
 *
 * The oscillator-based Web Audio path remained silent on real iPhones. A
 * normal audio element uses iOS's media playback path, including when the
 * hardware silent switch is on. Reusing the element matters just as much:
 * once a real tap has authorised it, timer- and socket-driven cues can use the
 * same playback session later.
 */

export enum Cue {
  Click = "click",
  TimeLow = "time-low",
  Submit = "submit",
  Success = "success",
  Failure = "failure",
  Reaction = "reaction",
}

const CUE_URL: Record<Cue, string> = {
  [Cue.Click]: "/audio/click.mp3",
  [Cue.TimeLow]: "/audio/time-low.mp3",
  [Cue.Submit]: "/audio/submit.mp3",
  [Cue.Success]: "/audio/success.mp3",
  [Cue.Failure]: "/audio/failure.mp3",
  [Cue.Reaction]: "/audio/reaction.mp3",
};

const DATA_CUES: Record<string, Cue | undefined> = {
  click: Cue.Click,
  submit: Cue.Submit,
  reaction: Cue.Reaction,
  none: undefined,
};

let audio: HTMLAudioElement | undefined;
let muted = false;
let playback = 0;
let ignoreClickUntil = 0;

const getAudio = () => {
  if (!audio) {
    audio = new Audio();
    audio.preload = "auto";
  }
  return audio;
};

export const setMuted = (value: boolean) => {
  muted = value;
  if (value) audio?.pause();
};

export const isMuted = () => muted;

/**
 * Every cue owns the media element immediately. This is especially important
 * for the crowd gasp: rapid emoji taps must restart it rather than queueing a
 * pile of gasps that continue after the reactions have left the screen.
 */
export const play = (cue: Cue) => {
  if (muted) return;

  const player = getAudio();
  const request = ++playback;
  player.pause();
  player.src = CUE_URL[cue];
  player.currentTime = 0;
  player.volume = 1;

  const attempt = player.play();
  if (!attempt) return;

  void attempt.catch(() => {
    // A newer cue deliberately aborts the older play promise. Only a failure
    // belonging to the current request matters, and sound is never the sole
    // signal for a game event.
    if (request !== playback) return;
  });
};

const targetElement = (event: Event): Element | null => {
  const target = event.target;
  if (target instanceof Element) return target;
  return target instanceof Node ? target.parentElement : null;
};

/**
 * Global interaction audio keeps ordinary controls consistent without adding
 * a sound call to every button. `data-sound` overrides the generic click for
 * lock-in and reaction controls, or suppresses it on the audition page.
 *
 * Mobile Safari emits a synthetic click after touchend. The touchend is the
 * strongest media-authorising gesture, so it plays the cue and the following
 * click is ignored rather than playing it twice.
 */
export const playInteraction = (event: Event) => {
  if (event.type === "click" && performance.now() < ignoreClickUntil) return;
  if (event.type === "touchend") ignoreClickUntil = performance.now() + 750;

  const target = targetElement(event);
  if (!target) return;

  const control = target.closest("button, a, [role='button']");
  if (!control) return;
  if (control instanceof HTMLButtonElement && control.disabled) return;

  const override = target.closest<HTMLElement>("[data-sound]")?.dataset.sound;
  if (override === "none") return;

  play((override && DATA_CUES[override]) || Cue.Click);
};

/** Best-effort only: iOS Safari has no vibration API. Never a sole signal. */
export const buzz = (pattern: number | number[]) => {
  if (muted) return;
  navigator.vibrate?.(pattern);
};
